import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  fetchActiveAnnouncements, ackAnnouncement, fetchAnnouncementAcks,
} from '../../lib/shiftApi';
import { sanitizeShiftHtml } from '../../lib/shiftSanitize';
import { useShift } from '../../context/ShiftContext';

function namesCsv(list) {
  const names = (list || []).map((s) => s.name || s.staffCode).filter(Boolean);
  return names.length ? names.join('、') : '無';
}

function AnnCard({ a, cardRefs, ackBusy, onAck, isMgr, ackInfo }) {
  return (
    <div
      ref={(el) => { if (cardRefs) cardRefs.current[a.id] = el; }}
      className={`shift-ann-card ${a.bucket === 'pending' && a.needAck ? 'unacked' : 'acked'}`}
    >
      <div className="shift-ann-title" style={a.textColor ? { color: a.textColor } : undefined}>{a.title}</div>
      {a.imageUrl ? <img className="shift-ann-img" src={a.imageUrl} alt="" /> : null}
      {a.content ? (
        <div className="shift-ann-body" dangerouslySetInnerHTML={{ __html: sanitizeShiftHtml(a.content) }} />
      ) : null}
      <div className="shift-ann-date">
        {a.confirmStart && a.confirmEnd ? `確認區間 ${a.confirmStart} ~ ${a.confirmEnd}` : null}
        {a.endDate ? `${a.confirmStart ? ' · ' : ''}顯示至 ${a.endDate}` : null}
      </div>
      <div className="shift-ann-actions">
        {a.needAck ? (
          <button
            type="button"
            className="shift-login-btn shift-ann-ack-btn"
            disabled={ackBusy === a.id}
            onClick={() => onAck(a.id)}
          >
            {ackBusy === a.id ? '確認中…' : '確認已讀'}
          </button>
        ) : (
          <span className="shift-ann-acked-tag">
            {a.bucket === 'history' ? '歷史' : '已確認'}
          </span>
        )}
      </div>
      {isMgr ? (
        <div className="shift-ann-ack-summary">
          {ackInfo?.loading ? (
            <div className="shift-ann-ack-line">確認名單載入中…</div>
          ) : ackInfo ? (
            <>
              <div className="shift-ann-ack-line">已確認：{namesCsv(ackInfo.acked)}</div>
              <div className="shift-ann-ack-line unacked">未確認：{namesCsv(ackInfo.unacked)}</div>
            </>
          ) : (
            <div className="shift-ann-ack-line">確認名單暫無法載入</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function ShiftAnnouncements({ onUnackedChange }) {
  const { isSupervisor, isOwner } = useShift();
  const isMgr = isSupervisor || isOwner;
  const [pending, setPending] = useState([]);
  const [confirmed, setConfirmed] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ackBusy, setAckBusy] = useState(null);
  const [acksById, setAcksById] = useState({});
  const cardRefs = useRef({});

  const loadAcksFor = useCallback(async (ids) => {
    if (!isMgr || !ids?.length) return;
    setAcksById((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = { ...(next[id] || {}), loading: true }; });
      return next;
    });
    const results = await Promise.all(ids.map(async (id) => {
      try {
        const r = await fetchAnnouncementAcks(id);
        if (r?.ok) return [id, { acked: r.acked || [], unacked: r.unacked || [], loading: false }];
      } catch { /* ignore */ }
      return [id, null];
    }));
    setAcksById((prev) => {
      const next = { ...prev };
      results.forEach(([id, info]) => { next[id] = info; });
      return next;
    });
  }, [isMgr]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchActiveAnnouncements();
      if (r?.ok) {
        const p = r.pending || (r.announcements || []).filter((a) => a.bucket === 'pending' || (!a.bucket && !a.acked));
        const c = r.confirmed || (r.announcements || []).filter((a) => a.bucket === 'confirmed' || (!a.bucket && a.acked));
        const h = r.history || (r.announcements || []).filter((a) => a.bucket === 'history');
        setPending(p);
        setConfirmed(c);
        setHistory(h);
        onUnackedChange?.(r.unackedCount ?? 0);
        if (isMgr) {
          const ids = [...p, ...c, ...h].map((a) => a.id).filter(Boolean);
          loadAcksFor(ids);
        }
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [onUnackedChange, isMgr, loadAcksFor]);

  useEffect(() => { load(); }, [load]);

  const onAck = async (id) => {
    setAckBusy(id);
    try {
      await ackAnnouncement(id);
      await load();
    } catch {
      alert('確認失敗');
    } finally {
      setAckBusy(null);
    }
  };

  if (loading) return <div className="shift-empty">載入公告…</div>;
  if (!pending.length && !confirmed.length && !history.length) {
    return <div className="shift-empty">目前沒有近日公告</div>;
  }

  return (
    <div className="shift-ann-list" lang="zh-Hant">
      {pending.length > 0 && (
        <section>
          <div className="shift-slabel" style={{ color: '#c4b5fd' }}>待確認</div>
          {pending.map((a) => (
            <AnnCard key={a.id} a={a} cardRefs={cardRefs} ackBusy={ackBusy} onAck={onAck} isMgr={isMgr} ackInfo={acksById[a.id]} />
          ))}
        </section>
      )}
      {confirmed.length > 0 && (
        <section>
          <div className="shift-slabel">已確認</div>
          {confirmed.map((a) => (
            <AnnCard key={a.id} a={a} cardRefs={cardRefs} ackBusy={ackBusy} onAck={onAck} isMgr={isMgr} ackInfo={acksById[a.id]} />
          ))}
        </section>
      )}
      {history.length > 0 && (
        <section>
          <div className="shift-slabel" style={{ color: 'var(--text3)' }}>歷史</div>
          {history.map((a) => (
            <AnnCard key={a.id} a={a} cardRefs={cardRefs} ackBusy={ackBusy} onAck={onAck} isMgr={isMgr} ackInfo={acksById[a.id]} />
          ))}
        </section>
      )}
    </div>
  );
}
